import React, { Component, Fragment } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import uniqid from "uniqid"
import { translate } from "react-i18next"
import { connect } from "react-redux"
import { compose } from "redux"
import { lighten } from "polished"
import * as filterActions from "../../state/actions/filterActions"
import Tooltip from "../themed-html-components/Tooltip"

const Label = styled.label`
  color: ${props => (props.isActive ? props.primaryColor : "#777")};
  font-weight: normal;
  font-size: 14px;
  margin: 0;
  cursor: pointer;
  float: left;
  &::selection {
    background: transparent;
  }
`

const TooltipIcon = styled.i`
  color: transparent;
  margin-left: 5px;
  cursor: pointer;
  float: left;
  &::selection {
    background: transparent;
  }
`

const CurrentValue = styled.a`
  font-size: 13px;
  font-style: italic;
  text-decoration: underline !important;
  text-decoration-style: dotted !important;
  float: right;
  padding-left: 5px;
  cursor: pointer;
  &::selection {
    background: transparent;
  }
`

const containerActiveBgColor = "#fafafa"
const FieldContainer = styled.div`
  padding: 5px 10px;
  margin-bottom: 2px;
  background-color: ${props => (props.isActive ? containerActiveBgColor : "transparent")};
  &:hover {
    background-color: ${containerActiveBgColor};
  }
  &:hover ${Label} {
    color: ${props => props.primaryColor};
  }
  &:hover ${TooltipIcon} {
    color: #ccc;
  }
`

const FieldEditControls = styled.div`
  padding-top: 5px;
  font-size: 13px;
`

const CollapseIcon = styled.i`
  color: ${props => lighten(0.2, props.primaryColor)};
  font-size: 10px;
`

const CollapseControl = styled.a`
  float: right;
  width: 10px;
  height: 10px;
  padding: 2px;
  margin-top: -10px;
  margin-right: -3px;
  &:hover {
    cursor: pointer;
  }
  &:hover ${CollapseIcon} {
    color: ${props => props.primaryColor} !important;
  }
`

/** Container for all filter fields in the sidebar */
class FilterField extends Component {
  static propTypes = {
    /** Child elements... the bespoke controls for selecting dates/generations etc */
    children: PropTypes.node,

    /** The filter object */
    filter: PropTypes.shape({
      /** The current display value for this field */
      display: PropTypes.shape({
        i18nKey: PropTypes.string /** The i18n key for the selected value - e.g. Review name */,
        text: PropTypes.string /** The i18n key for the selected value, e.g. formatted start date */
      }).isRequired
    }).isRequired,

    /** The i18n key to translate for the main field label */
    labelKey: PropTypes.string.isRequired,

    /** Whether or not this is a multiple choice field - dictates no value label text */
    multipleChoice: PropTypes.bool,

    /** The translate function for i18n */
    t: PropTypes.func.isRequired,
    /** The i18n key for the tooltip content */
    tooltipKey: PropTypes.string,
    /** theme from the user details */
    theme: PropTypes.shape({
      primaryColor: PropTypes.string.isRequired,
      primaryColorHover: PropTypes.string.isRequired,
      navBackgroundColor: PropTypes.string.isRequired
    }).isRequired
  }

  static defaultProps = {
    children: null,
    multipleChoice: false,
    tooltipKey: null
  }

  state = {
    tooltipOpen: false
  }

  componentWillMount = () => {
    /** A unique id is needed to tie together the label and its input control,
     * and for tooltip and its trigger element */
    const uniqueId = uniqid()
    this.tooltipId = `${uniqueId}_ttp`
  }

  toggleFieldActive = () => {
    // eslint-disable-next-line react/prop-types
    this.props.setActiveField(this.props.isActive ? null : this.props.labelKey)
  }

  toggleTooltip = () => {
    this.setState({ tooltipOpen: !this.state.tooltipOpen })
  }

  render() {
    const {
      t,
      labelKey,
      children,
      tooltipKey,
      multipleChoice,
      filter,
      isActive,
      theme
    } = this.props

    let currentValue = filter.display.i18nKey ? t(filter.display.i18nKey) : filter.display.text
    if (!currentValue) {
      currentValue = multipleChoice
        ? t("Filters.NoSelectionText.MultipleChoice")
        : t("Filters.NoSelectionText.SingleChoice")
    }

    return (
      <FieldContainer isActive={isActive} primaryColor={theme.primaryColor}>
        <div>
          <Label
            onClick={this.toggleFieldActive}
            isActive={isActive}
            primaryColor={theme.primaryColor}
          >
            {t(labelKey)}
          </Label>
          {tooltipKey && (
            <Fragment>
              <TooltipIcon
                className="fa fa-info-circle"
                data-tip={t(tooltipKey)}
                data-for={this.tooltipId}
                data-html
              />
              <Tooltip id={this.tooltipId} />
            </Fragment>
          )}
          {!isActive && (
            <CurrentValue onClick={this.toggleFieldActive}>{t(currentValue)}</CurrentValue>
          )}
          {isActive && (
            <CollapseControl
              onClick={this.toggleFieldActive}
              primaryColor={theme.primaryColor}
              title={t("Filters.CloseFilter")}
            >
              <CollapseIcon className="fa fa-times" primaryColor={theme.primaryColor} />
            </CollapseControl>
          )}
          <div className="clearfix" />
        </div>
        {isActive && <FieldEditControls>{children}</FieldEditControls>}
      </FieldContainer>
    )
  }
}

function mapStateToProps(state, ownProps) {
  return {
    /** Whether or not this field is being edited (so is expanded) */
    isActive: state.filters.activeField === ownProps.labelKey,
    theme: state.userDetail.theme
  }
}

function mapDispatchToProps(dispatch) {
  return {
    setActiveField: fieldKey => dispatch(filterActions.setActiveField(fieldKey))
  }
}

export default compose(translate(), connect(mapStateToProps, mapDispatchToProps))(FilterField)



// WEBPACK FOOTER //
// ./src/components/filters/FilterField.jsx